import React, { useState, useEffect, useRef } from 'react';
import { Button, Checkbox } from 'antd';
import { PlusCircleOutlined, CloseOutlined } from '@ant-design/icons';

interface MultiSelectWithAddItemProps {
  label: string;
  options: string[];
  selected: string[];
  setSelected: (selected: string[]) => void;
  otherInputValue?: string;
  setOtherInputValue?: (value: string) => void;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  showCheckBox?: boolean;
  showAddCustom?: boolean;
  isFromBrands?: boolean;
  contextKey?: string;
  customOptionsMap: Record<string, string[]>;
  setCustomOptionsMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;

  activeMarket?: string; // ✅ New prop
  error?: string; // ✅ New prop
}

const MultiSelectWithAddItem: React.FC<MultiSelectWithAddItemProps> = ({
  label,
  options = [],
  selected = [],
  otherInputValue,
  setOtherInputValue,
  checked,
  setSelected,
  setChecked,
  contextKey,
  showCheckBox = true,showAddCustom=false,customOptionsMap,setCustomOptionsMap,
  activeMarket,
  error = "",
}) => {
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [newCustomOption, setNewCustomOption] = useState("");
  const inputRef = useRef<HTMLFormElement>(null);
  // const customOptions = contextKey ? customOptionsMap[contextKey] || [] : [];
  // const customKey = `${activeMarket}_${contextKey}`; // ✅ Market-specific key
  const customKey = activeMarket ? `${activeMarket}_${contextKey}` : contextKey;
  const customOptions = customKey ? customOptionsMap[customKey] || [] : [];
  
  const updateCustomOptions = (updated: string[]) => {
    if (contextKey) {
      setCustomOptionsMap((prev) => ({
        ...prev,
        [customKey || ""]: updated,
      }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isAddingCustom &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        if (newCustomOption.trim()) {
          addCustomOption();
        } else {
          setIsAddingCustom(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAddingCustom, newCustomOption]);

  const toggleSelection = (option: string) => {
    if (option === "None") {
      setSelected(["None"]);
    } else {
      const updated = selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected.filter((item) => item !== "None"), option];
      setSelected(updated);
    }
  };

  const addCustomOption = () => {
    const trimmed = newCustomOption.trim();
    if (
      trimmed &&
      !customOptions.includes(trimmed) &&
      !options.includes(trimmed)
    ) {
      updateCustomOptions([...customOptions, trimmed]);
      setSelected([...selected, trimmed]);
    }
    setNewCustomOption("");
    setIsAddingCustom(false);
  };

  const removeCustomOption = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    updateCustomOptions(customOptions.filter((item) => item !== option));
    setSelected(selected.filter((item) => item !== option));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomOption();
    } else if (e.key === "Escape") {
      setIsAddingCustom(false);
      setNewCustomOption("");
    }
  };

  return (
    <div className="col-span-1">
      {showCheckBox ? (
        <Checkbox
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="common-bold-label"
        >
          {label} {error !== ""&& (
          <div className="text-red-500 text-sm !my-0">{error}</div>
        )}
        </Checkbox>
       
      ) : (
        <p className="common-bold-label">
          {label}
          {error !== ""&& (
            <div className="text-red-500 text-sm !my-0">{error}</div>
          )}
        </p>
      )}

      {checked && (
        <div className="flex flex-wrap gap-2 mt-6">
          {[...options, ...customOptions].map((option) => (
            <Button
              key={option}
              type="text"
              onClick={() => {
                if (selected.includes(option) && option === "Other") return;
                toggleSelection(option);
              }}
              className={`rounded-[30px] px-[20px] py-[10px] text-sm transition-all font-['Inter'] leading-tight flex items-center gap-1 ${
                selected.includes(option) ? "text-[#1849a9]" : "text-[#1d2939]"
              }`}
              style={{
                border: `1px solid ${
                  selected.includes(option) ? "#53b1fd" : "#e4e7ec"
                }`,
                backgroundColor: selected.includes(option) ? "#d1e9ff" : "",
              }}
            >
              {selected.includes(option) && option === "Other" ? (
                <input
                  type="text"
                  placeholder="Your Answer"
                  className="outline-none border-none bg-transparent text-[#1d2939] text-sm"
                  value={otherInputValue}
                  onChange={(e) =>
                    setOtherInputValue && setOtherInputValue(e.target.value)
                  }
                />
              ) : (
                <>
                  {option}
                  {customOptions.includes(option) && (
                    <CloseOutlined
                      onClick={(e) => removeCustomOption(option, e)}
                      className="text-xs ml-1 hover:text-red-600"
                    />
                  )}
                </>
              )}
            </Button>
          ))}

          {showAddCustom &&
            (isAddingCustom ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  addCustomOption();
                }}
                className="flex items-center"
                ref={inputRef}
              >
                <input
                  type="text"
                  value={newCustomOption}
                  onChange={(e) => setNewCustomOption(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Enter custom category"
                  className="px-3 py-1 border border-gray-300 rounded-l-full text-sm focus:outline-none h-[32px]"
                  autoFocus
                />
                <Button
                  htmlType="submit"
                  className="rounded-r-full common-primary-button"
                >
                  Add
                </Button>
              </form>
            ) : (
              <Button
                type="dashed"
                className="flex items-center gap-1 border-dashed rounded-[30px] text-sm"
                onClick={() => setIsAddingCustom(true)}
              >
                <PlusCircleOutlined /> Add Other
              </Button>
            ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectWithAddItem;
